import React from 'react';
import { HStack, Tag } from '@chakra-ui/react';
import { type JsiInterceptorRequest, type JsiInterceptorResponse } from '../interface/Crash';

export type RequestType = 'JSI' | 'HTTP' | 'WS' | 'Starfish';
const RequestTypeColors: Record<RequestType, string> = {
    JSI: 'yellow',
    HTTP: 'blue',
    WS: 'purple',
    Starfish: 'pink',
};

export type RequestMethod = 'GET' | 'POST' | 'PUT' | 'OPTIONS' | 'DELETE';
const RequestMethodColors: Record<RequestMethod, string> = {
    GET: 'green',
    POST: 'pink',
    PUT: 'yellow',
    DELETE: 'red',
    OPTIONS: 'gray',
};
export type RequestJsiInterceptorKind = 'fetch' | 'xhr';
const RequestJsiInterceptorKindColors: Record<RequestJsiInterceptorKind, string> = {
    fetch: 'blue',
    xhr: 'yellow',
};

const protocolSessionIdColors: string[][] = [
    // backgroundColor, fontColor
    ['#FF0000', '#FFFFFF'],
    ['#FF8000', '#FFFFFF'],
    ['#FFFF00', '#000000'],
    ['#80FF00', '#000000'],
    ['#00FF00', '#000000'],
    ['#00FF80', '#000000'],
    ['#00FFFF', '#000000'],
    ['#0080FF', '#FFFFFF'],
    ['#0000FF', '#FFFFFF'],
    ['#7F00FF', '#FFFFFF'],
    ['#BF00FF', '#FFFFFF'],
    ['#FF00FF', '#FFFFFF'],
    ['#FF0080', '#FFFFFF'],
];

export const RequestTypeTag: React.FC<{
    value: RequestType | '';
}> = ({ value }) => {
    if (value === '' || value === undefined || value === null) {
        return <></>;
    }
    const color = RequestTypeColors[value] || 'gray';
    return (
        <Tag variant="subtle" colorScheme={color}>
            {value}
        </Tag>
    );
};

export const RequestMethodTag: React.FC<{
    value: RequestMethod | '';
}> = ({ value }) => {
    if (value === '' || value === undefined || value === null) {
        return <></>;
    }
    const color = RequestMethodColors[value] || 'gray';
    return (
        <Tag variant="outline" colorScheme={color}>
            {value}
        </Tag>
    );
};
export const RequestJsiInterceptorKindTag: React.FC<{
    value: RequestJsiInterceptorKind | '';
}> = ({ value }) => {
    if (value === '' || value === undefined || value === null) {
        return <></>;
    }
    const color = RequestJsiInterceptorKindColors[value] || 'gray';
    return (
        <Tag variant="solid" colorScheme={color}>
            {value}
        </Tag>
    );
};

export const RequestStatusTag: React.FC<{ value: string | number }> = ({ value }) => {
    if (value === '' || value === undefined || value === null) {
        return <></>;
    }
    let color = 'gray';
    const code = typeof value === 'string' ? parseInt(value) : value;
    if (code < 100) {
        color = 'gray';
    } else if (code < 200) {
        color = 'cyan';
    } else if (code < 300) {
        color = 'green';
    } else if (code < 400) {
        color = 'blue';
    } else if (code < 500) {
        color = 'yellow';
    } else if (code < 600) {
        color = 'red';
    }
    return (
        <Tag variant="solid" colorScheme={color}>
            {value}
        </Tag>
    );
};

export const RequestUrlTag: React.FC<{ value: string }> = ({ value }) => {
    if (value === '' || value === undefined || value === null) {
        return <></>;
    }
    return (
        <Tag variant="subtle" colorScheme="gray" whiteSpace="normal" wordBreak="break-word">
            {value}
        </Tag>
    );
};

export const ProtocolSessionIdTag: React.FC<{
    index: number;
}> = ({ index }) => {
    if (index === undefined || index === null) {
        return <></>;
    }
    const color = protocolSessionIdColors[index % protocolSessionIdColors.length];
    const value = String.fromCharCode(65 + index);
    return (
        <Tag variant="solid" bg={color[0]} color={color[1]}>
            {value}
        </Tag>
    );
};

export const JsiInterceptorBodySizeRow: React.FC<{ obj: JsiInterceptorRequest | JsiInterceptorResponse }> = ({
    obj,
}) => {
    const sizeKb = (obj.bodySize ?? 0) / 1000;
    const truncated = obj.bodyTruncated ?? false;
    return (
        <HStack spacing={2}>
            {sizeKb && (
                <Tag variant="solid" colorScheme="blue">
                    {sizeKb} KB
                </Tag>
            )}
            {truncated && (
                <Tag variant="solid" colorScheme="red">
                    TRUNCATED
                </Tag>
            )}
            {obj.bodyEncoding && (
                <Tag variant="outline" colorScheme="yellow">
                    {obj.bodyEncoding}
                </Tag>
            )}
        </HStack>
    );
};
